import { Tablist as FluentTablist } from "@fluentui/web-components";
import { TablistEventNames } from "./tablist.options.js";
/**
 * Tablist
 * @summary A custom Tablist component based on Fluent UI's Tablist, enhanced with custom styling and behavior.
 *
 * @example
 * ```html
 * <fabric-tablist appearance="subtle" size="medium" orientation="horizontal" disabled="false" activeid="tab1">
 *   <fabric-tab slot="tab">Tab 1</fabric-tab>
 *   <fabric-tab slot="tab">Tab 2</fabric-tab>
 *   <fabric-tab slot="tab">Tab 3</fabric-tab>
 * </fabric-tablist>
 * ```
 *
 * @attr {string} appearance - There are two modes of appearance: transparent and subtle.
 * @attr {string} size - Used to set the size of all the tab controls, which affects text size and margins. Three sizes: small, medium, and large.
 * @attr {string} orientation - The orientation, which can be 'horizontal' or 'vertical'.
 * @attr {boolean} disabled - Used for disabling all click and keyboard events for the tabs, child tab elements.
 * @attr {string} activeid - The id of the active tab.
 *
 * @prop {TablistAppearance} appearance - There are two modes of appearance: transparent and subtle.
 * @prop {TablistSize} size - Used to set the size of all the tab controls, which affects text size and margins. Three sizes: small, medium, and large.
 * @prop {TablistOrientation} orientation - The orientation, which can be 'horizontal' or 'vertical'.
 * @prop {boolean} disabled - Used for disabling all click and keyboard events for the tabs, child tab elements.
 * @prop {string} activeid - The id of the active tab.
 *
 * @slot tab - Slot for individual tabs.
 *
 * @cssprop --tabIndicatorInsetBlock - Controls the block inset for the active tab indicator.
 * @cssprop --tabIndicatorInsetInline - Controls the inline inset for the active tab indicator.
 * @cssprop --tabIndicatorOffset - Offsets the active tab indicator from its default position.
 * @cssprop --tabIndicatorScale - Scales the active tab indicator size.
 * @cssprop --tabPaddingBlock - Sets the block padding for each tab.
 * @cssprop --tabPaddingInline - Sets the inline padding for each tab.
 *
 * @method activeidChanged - Initiates the active tab indicator animation loop when activeid changes.
 * @method activeTabIndexPublic - Returns the index of the currently active tab based on the activeid property.
 * @method tabsChanged - Initiates the active tab indicator animation loop when tabs change.
 * @method setTabs - Function that is invoked whenever the selected tab or the tab collection changes.
 * @method adjust - The adjust method for FASTTabs.
 *
 * @fires active-index-change - Event fired when the active tab index changes, providing the new index in the event detail.
 * @fires change - Event fired when the active tab id changes, providing the new id in the event detail.
 *
 * @extends FluentTablist
 * @tagname fabric-tablist
 * @public
 */
export class Tablist extends FluentTablist {
    get activeTabIndexPublic() {
        if (!this.tabs || !this.activeid)
            return -1;
        return this.tabs.findIndex((tab) => tab.id === this.activeid);
    }
    emitActiveIndexChange(activeTabIndex) {
        this.dispatchEvent(new CustomEvent(TablistEventNames.activeIndexChange, {
            detail: { activeTabIndex },
            bubbles: true,
            composed: true
        }));
    }
    // Example: emit event with the active tab index
    activeidChanged(oldValue, newValue) {
        super.activeidChanged(oldValue, newValue);
        this.emitActiveIndexChange(this.activeTabIndexPublic);
    }
}
//# sourceMappingURL=tablist.js.map